import { useState, useRef, useEffect } from 'react';
import { PaymentVolumeRow } from './types';
import { exportPaymentVolumeToXlsx, exportPaymentVolumeToCsv } from './paymentsExportUtils';
import { Export } from '@phosphor-icons/react';
import { DillButton, DillTable } from '../../../../general/Dill';
import type { DillColumn } from '../../../../general/Dill';

// ─── Column helpers ───────────────────────────────────────────────────────────

const numericColumn = (
  key: keyof PaymentVolumeRow,
  label: string,
  width = 160
): DillColumn<PaymentVolumeRow> => ({
  key,
  label,
  width,
  sortable: true,
  numeric: true,
  headerAlign: 'right',
  render: (value: number) => {
    if (value === 0 || value == null) {
      return <div className="text-xs text-gray-300 text-right">—</div>;
    }
    return (
      <div className="text-xs font-medium text-right tabular-nums text-gray-700">
        $
        {value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
    );
  },
});

const columns: DillColumn<PaymentVolumeRow>[] = [
  {
    key: 'monthLabel',
    label: 'Month/Year',
    width: 140,
    sortable: true,
    render: (value: string) => <div className="text-xs font-medium text-gray-700">{value}</div>,
  },
  numericColumn('totalVolume', 'Total Volume', 170),
  numericColumn('cardVolume', 'Card Volume', 160),
  numericColumn('bankVolume', 'Bank ACH Volume', 170),
];

// ─── Component ─────────────────────────────────────────────────────────────────

interface PaymentVolumeTableProps {
  data: PaymentVolumeRow[];
  isLoading?: boolean;
  year: string;
}

export const PaymentVolumeTable = ({ data, isLoading, year }: PaymentVolumeTableProps) => {
  const [isExportOpen, setIsExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setIsExportOpen(false);
      }
    };

    if (isExportOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportOpen]);

  const handleExportXlsx = () => {
    exportPaymentVolumeToXlsx(data);
    setIsExportOpen(false);
  };

  const handleExportCsv = () => {
    exportPaymentVolumeToCsv(data);
    setIsExportOpen(false);
  };

  const totals = data.reduce(
    (acc, row) => ({
      totalVolume: acc.totalVolume + row.totalVolume,
      cardVolume: acc.cardVolume + row.cardVolume,
      bankVolume: acc.bankVolume + row.bankVolume,
    }),
    { totalVolume: 0, cardVolume: 0, bankVolume: 0 }
  );

  const footerColumns: DillColumn<PaymentVolumeRow>[] = columns.map((col) =>
    col.key === 'monthLabel'
      ? { ...col, footer: <span className="text-gray-700 font-semibold text-xs">Total</span> }
      : {
          ...col,
          footer: (
            <span className="text-gray-700 font-semibold text-xs">
              $
              {totals[col.key as 'totalVolume' | 'cardVolume' | 'bankVolume'].toLocaleString(
                'en-US',
                { minimumFractionDigits: 2, maximumFractionDigits: 2 }
              )}
            </span>
          ),
        }
  );

  return (
    <div className="p-6 flex flex-col">
      <div className="mb-4">
        <p className="text-xs text-gray-500">
          Total payment volume processed through Dill for {year}, broken down by card and bank ACH,
          month by month.
        </p>
      </div>

      <DillTable<PaymentVolumeRow>
        columns={footerColumns}
        data={data}
        rowKey={(r) => r.id}
        verticalDividers
        rowHeight={35}
        scrollX
        striped={false}
        maxHeight="calc(100vh - 280px)"
        loading={isLoading}
        loadingText="Loading payment volume..."
        emptyMessage="No payment volume for this year"
        showCount={false}
        showFooter
        toolbarRight={
          <div
            className="relative"
            ref={exportRef}
            title={isLoading ? 'Still loading…' : undefined}>
            <DillButton
              label="Export"
              onClick={() => setIsExportOpen(!isExportOpen)}
              icon={<Export size={14} />}
              variant="primary"
              isDisabled={isLoading}
            />
            {isExportOpen && (
              <div className="absolute right-0 z-50 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-40">
                <button
                  onClick={handleExportXlsx}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                  Export as XLSX
                </button>
                <button
                  onClick={handleExportCsv}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-100/80 focus:text-blue-600">
                  Export as CSV
                </button>
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};
